import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, XCircle, CircleDashed, Loader2, Save, Trash2, ShieldCheck, ExternalLink } from "lucide-react";
import { SOCIAL_ICONS } from "../data/socialIcons";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "digsby_admin_token";

const STATUS_META = {
  verified: { label: "Verified", cls: "bg-[#00A000]/10 text-[#00A000] border-[#00A000]/30", Icon: CheckCircle2 },
  broken: { label: "Broken link", cls: "bg-[#FF0000]/10 text-[#FF0000] border-[#FF0000]/30", Icon: XCircle },
  not_set: { label: "Not set", cls: "bg-slate-100 text-slate-500 border-black/10", Icon: CircleDashed },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.not_set;
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold ${m.cls}`} data-testid={`social-status-${status}`}>
      <Icon size={14} /> {m.label}
    </span>
  );
}

function SocialRow({ row, authHeader, onChanged }) {
  const brand = SOCIAL_ICONS[row.platform] || {};
  const Icon = brand.Icon;
  const [url, setUrl] = useState(row.url || "");
  const [status, setStatus] = useState(row.status || "not_set");
  const [reason, setReason] = useState(row.reason || "");
  const [preview, setPreview] = useState(row.preview || {});
  const [published, setPublished] = useState(!!row.published);
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const dirty = url.trim() !== (row.url || "").trim();
  const savedVerified = row.status === "verified" && row.url;

  const verify = async () => {
    if (!url.trim()) {
      toast.error("Enter a URL first.");
      return;
    }
    setVerifying(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/social/verify`, { platform: row.platform, url }, authHeader);
      setStatus(data.status);
      setReason(data.reason || "");
      setPreview(data.preview || {});
      if (data.url) setUrl(data.url);
      if (data.status === "verified") toast.success(`${row.name} link verified!`);
      else toast.error(data.reason || "Could not verify this link.");
    } catch (err) {
      if (err.response?.status === 401) onChanged(true);
      else toast.error("Verification failed. Try again.");
    } finally {
      setVerifying(false);
    }
  };

  const save = async () => {
    if (status !== "verified") {
      toast.error("Please verify the link before saving.");
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/admin/social/${row.platform}`,
        { platform: row.platform, url, status, published: true, preview },
        authHeader
      );
      setPublished(true);
      toast.success(`${row.name} saved and shown in footer.`);
      onChanged();
    } catch (err) {
      if (err.response?.status === 401) onChanged(true);
      else toast.error("Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      await axios.delete(`${API}/api/admin/social/${row.platform}`, authHeader);
      setUrl("");
      setStatus("not_set");
      setReason("");
      setPreview({});
      setPublished(false);
      toast.success(`${row.name} removed from footer.`);
      onChanged();
    } catch (err) {
      if (err.response?.status === 401) onChanged(true);
      else toast.error("Could not remove. Try again.");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="rounded-3xl border-2 border-black/10 bg-white p-5" data-testid={`social-row-${row.platform}`}>
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <span
            className="w-11 h-11 rounded-full flex items-center justify-center text-white shrink-0"
            style={{ backgroundColor: brand.color || "#333" }}
          >
            {Icon && <Icon size={20} />}
          </span>
          <div>
            <p className="font-heading font-bold text-lg leading-tight">{row.name}</p>
            <div className="mt-1 flex items-center gap-2">
              <StatusBadge status={status} />
              {published && (
                <span className="inline-flex items-center gap-1 text-xs font-bold text-[#0000FF]" data-testid={`social-live-${row.platform}`}>
                  <ShieldCheck size={13} /> In footer
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <input
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            if (status !== "not_set") setStatus("not_set");
          }}
          placeholder={brand.placeholder || "https://..."}
          data-testid={`social-input-${row.platform}`}
          className="flex-1 min-h-[48px] rounded-2xl border-2 border-black/15 px-4 font-medium outline-none focus:border-[#0000FF]"
        />
        <button
          onClick={verify}
          disabled={verifying}
          data-testid={`social-verify-${row.platform}`}
          className="inline-flex items-center justify-center gap-2 min-h-[48px] rounded-full border-2 border-black/15 px-5 font-bold hover:bg-[#FBEF14] transition-colors disabled:opacity-60"
        >
          {verifying ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Verify
        </button>
        <button
          onClick={save}
          disabled={saving || status !== "verified"}
          data-testid={`social-save-${row.platform}`}
          className="inline-flex items-center justify-center gap-2 min-h-[48px] rounded-full bg-[#00A000] text-white px-5 font-bold hover:bg-[#008000] transition-colors disabled:opacity-40"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
        </button>
        {(savedVerified || published) && (
          <button
            onClick={remove}
            disabled={removing}
            data-testid={`social-remove-${row.platform}`}
            className="inline-flex items-center justify-center gap-2 min-h-[48px] rounded-full border-2 border-[#FF0000]/30 text-[#FF0000] px-5 font-bold hover:bg-[#FF0000] hover:text-white transition-colors disabled:opacity-60"
          >
            {removing ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />} Remove
          </button>
        )}
      </div>

      {reason && status === "broken" && (
        <p className="mt-2 text-sm text-[#FF0000] font-medium" data-testid={`social-reason-${row.platform}`}>{reason}</p>
      )}
      {status === "not_set" && dirty && url.trim() && (
        <p className="mt-2 text-sm text-slate-500 font-medium">Verify this link before saving.</p>
      )}

      {status === "verified" && (preview.title || preview.image) && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`social-preview-${row.platform}`}
          className="mt-4 flex items-center gap-3 rounded-2xl border-2 border-black/10 bg-[#FFFBF2] p-3 hover:border-[#0000FF] transition-colors"
        >
          {preview.image && (
            <img src={preview.image} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
          )}
          <div className="min-w-0">
            <p className="font-heading font-bold text-sm truncate">{preview.title || row.name}</p>
            <p className="text-xs text-slate-500 truncate flex items-center gap-1"><ExternalLink size={11} /> {url}</p>
          </div>
        </a>
      )}
    </div>
  );
}

export default function SocialManager({ onAuthError }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem(TOKEN_KEY);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/social`, authHeader);
      setRows(data);
    } catch (err) {
      if (err.response?.status === 401) onAuthError();
      else toast.error("Could not load social links.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleChanged = (authFail) => {
    if (authFail === true) return onAuthError();
    load();
  };

  return (
    <div>
      <h1 className="font-heading font-bold text-3xl sm:text-4xl mb-2">Social Links</h1>
      <p className="text-slate-600 mb-8 max-w-2xl">
        Add your social profiles below. Verify each link (we check the domain and that the page loads),
        then Save to show it in the site footer. Only verified links can be saved.
      </p>
      {loading ? (
        <div className="flex items-center gap-2 text-slate-500" data-testid="social-loading">
          <Loader2 size={18} className="animate-spin" /> Loading…
        </div>
      ) : (
        <div className="grid gap-4" data-testid="social-manager">
          {rows.map((row) => (
            <SocialRow key={row.platform} row={row} authHeader={authHeader} onChanged={handleChanged} />
          ))}
        </div>
      )}
    </div>
  );
}
